import {useIntl} from 'react-intl'
import {isPastDate} from './date'

/**
 * Provides localized validation messages for a birthday input field.
 *
 * @returns {Object} An object containing validation messages:
 *   - `required`: Message indicating that the date of birth is required.
 *   - `invalid`: Message indicating that the entered date of birth is invalid.
 *   - `hasLegalAge`: Message indicating that the user must be at least 16 years old.
 */
export const useBirthdayValidationMessages = () => {
    const {formatMessage} = useIntl()

    return {
        required: formatMessage({
            defaultMessage: 'Please enter your date of birth.',
            id: 'birthday_field.error.required_birthday'
        }),
        invalid: formatMessage({
            defaultMessage: 'Please enter a valid date of birth.',
            id: 'birthday_field.error.invalid_birthday'
        }),
        hasLegalAge: (ageLimit) =>
            formatMessage(
                {
                    defaultMessage: 'You must be at least {ageLimit} years old.',
                    id: 'birthday_field.error.legal_birthday'
                },
                {ageLimit}
            )
    }
}

/**
 * Validates if the given birth date meets the legal age requirement.
 *
 * @param {string} value - The birth date to validate in ISO format (YYYY-MM-DD).
 * @param {number} [ageLimit=16] - The minimum legal age to validate against. Defaults to 16.
 * @returns {boolean} - Returns true if the age is greater than or equal to the age limit, otherwise false.
 */
const validateAge = (value, ageLimit = 16) => {
    const today = new Date()
    const birthDate = new Date(value)
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDifference = today.getMonth() - birthDate.getMonth()

    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1 >= ageLimit
    }
    return age >= ageLimit
}

/**
 * Checks if the given birth date meets the legal age requirement and is a date in the past.
 *
 * @param {string} value - The birth date to validate in ISO format (YYYY-MM-DD).
 * @returns {Object} - An object containing two boolean properties:
 *                     - `isPastDate`: true if the birth date is in the past.
 *                     - `hasLegalAge`: a function that takes `ageLimit` and returns true if the age is greater than or equal to the age limit.
 */
export const validateBirthday = (value) => {
    return {
        isPastDate: !value ? true : isPastDate(value),
        hasLegalAge: (ageLimit) => (!value ? true : validateAge(value, ageLimit))
    }
}
